/**
 * 埋め込みの表示の型(#283)。
 *
 * 正本は [#33 の DESIGN.md](../../../../docs/issues/33-viewer-embed/DESIGN.md) の「`minimal`」。
 *
 * **`minimal` は、埋め込み先が素材を API で入れる構成のための表示。** 利用者向けの案内や
 * ドロップの操作を出したままだと、**利用者にできない操作を案内する**ことになる
 * (連携先と合意した表 —— 同意書 [283-embed-minimal-ui](../../../../docs/issues/283-embed-minimal-ui/AGREEMENT.md))。
 *
 * **判定をシェルから分けている**のは、DOM を用意せずテストできるようにするため
 * ([ADR-0019](../../../../docs/adr/0019-dev-foundation.md) が jsdom を導入しない方針)。
 */

/** 表示の型。**既定は `default`**(属性が無い・未知の値)。 */
export type UiMode = 'default' | 'minimal';

/** 出すもの・受けるもの。**`true` が今までの表示。** */
export interface UiParts {
  /** 空のセルの輪郭と案内(「ここにドロップ」/ タッチ端末の「PC で試せます」—— #208)。 */
  readonly emptyCellHints: boolean;
  /** **WebGL2 非対応時の**全枠の輪郭(フォールバック描画 —— #33)。 */
  readonly fallbackOutlines: boolean;
  /** ドロップの受け口(「ここにドロップ」の帯・ドラッグ中のセルの強調 —— #124 / #152)。 */
  readonly dropTarget: boolean;
  /**
   * **ドロップで素材を入れるか。**
   *
   * `false` でも `dragover` / `drop` の `preventDefault` は続ける —— やめると
   * **ブラウザがファイルを開き、埋め込み先のページから離れる**(#159 で起きた)。
   */
  readonly acceptsDrop: boolean;
  /** 「素材はサーバに送信されません」の注意(#159)。 */
  readonly noUploadNotice: boolean;
  /** 「画像を保存」(#180)。**動画の書き出しはここでは決めない**(`data-signa-export` と共有リンクの設定 —— 同意書 Q1)。 */
  readonly saveImage: boolean;
  /**
   * **WebGL2 非対応時の当システムの文言。** `minimal` では埋め込み先が自分の案内を出す。
   * **`signa:error`(`UNSUPPORTED`)は型によらず出す。**
   */
  readonly unsupportedMessage: boolean;
}

const DEFAULT_PARTS: UiParts = {
  emptyCellHints: true,
  fallbackOutlines: true,
  dropTarget: true,
  acceptsDrop: true,
  noUploadNotice: true,
  saveImage: true,
  unsupportedMessage: true,
};

/**
 * `minimal` で消すもの(連携先と合意した表)。
 *
 * **再生 / 停止 / 同期は残す。** 読み込み中・期限切れ・自動再生の拒否などの文言も残す
 * (表に無いもの —— 同意書の仮定 2)。
 */
const MINIMAL_PARTS: UiParts = {
  emptyCellHints: false,
  fallbackOutlines: false,
  dropTarget: false,
  acceptsDrop: false,
  noUploadNotice: false,
  saveImage: false,
  unsupportedMessage: false,
};

export function uiParts(mode: UiMode): UiParts {
  return mode === 'minimal' ? MINIMAL_PARTS : DEFAULT_PARTS;
}

/**
 * 属性・指定の値を読む。**認めるのは `minimal` と `default`**(大文字小文字を区別する)。
 * それ以外は `null`(呼び出し側で次の候補に落とす —— 未知の値は既定の表示になる)。
 */
export function parseUiMode(raw: string | null | undefined): UiMode | null {
  return raw === 'minimal' ? 'minimal' : raw === 'default' ? 'default' : null;
}
